import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import { SearchableSelect } from '../../components/ui/searchable-select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Plus, Receipt, Trash2, Send, Edit, Eye, X, AlertCircle,
  Clock, CheckCircle, XCircle, Banknote, FileText, Calendar, RefreshCw, Search,
  Phone, Download, BadgeCheck, Loader2, FileSearch, ArrowRight, ArrowLeft, Upload, CheckCircle2, CreditCard, Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import AttachmentPreviewModal from '../../components/AttachmentPreviewModal';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800 border-gray-200',
  pending: 'bg-blue-100 text-blue-800 border-blue-200',
  approved: 'bg-green-100 text-green-800 border-green-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
  // "returned" needs attention (send back to maker) — bright amber
  returned: 'bg-amber-100 text-amber-900 border-amber-300 ring-1 ring-amber-200',
  completed: 'bg-purple-100 text-purple-800 border-purple-200',
  // "retired" is a soft archived state — neutral slate to clearly distinguish
  // from "returned" (previously they shared the same orange styling)
  retired: 'bg-slate-100 text-slate-600 border-slate-300',
};

const STATUS_LABELS = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  pending_authorization: 'Pending Authorization',
  pending_disbursement: 'Pending Disbursement',
  completed: 'Disbursed',
  retired: 'Retired',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  returned: 'Returned',
};

const PRIORITY_COLORS = {
  Low: 'bg-gray-100 text-gray-700',
  Normal: 'bg-blue-100 text-blue-700',
  High: 'bg-orange-100 text-orange-700',
  Urgent: 'bg-red-100 text-red-700',
};

const CATEGORIES = [
  { value: 'Travel', label: 'Travel' },
  { value: 'Meals & Entertainment', label: 'Meals & Entertainment' },
  { value: 'Office Supplies', label: 'Office Supplies' },
  { value: 'Equipment', label: 'Equipment' },
  { value: 'Software & Subscriptions', label: 'Software & Subscriptions' },
  { value: 'Training & Development', label: 'Training & Development' },
  { value: 'Communication', label: 'Communication' },
  { value: 'Transport', label: 'Transport' },
  { value: 'Accommodation', label: 'Accommodation' },
  { value: 'Medical', label: 'Medical' },
  { value: 'Utilities', label: 'Utilities' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'Administrative', label: 'Administrative' },
  { value: 'Miscellaneous', label: 'Miscellaneous' },
];

const MyExpenses = () => {
  const { getAuthHeaders, currentOffice } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState(CATEGORIES);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null); // Track expense being edited
  const [saving, setSaving] = useState(false);

  // Payment Voucher states
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [voucherStep, setVoucherStep] = useState('form');
  const [voucherData, setVoucherData] = useState({
    payee_name: '', payee_phone: '', payee_email: '', amount_paid: '',
    purpose: '', payment_mode: 'cash', transfer_channel: '', transfer_account: '',
  });
  const [voucherId, setVoucherId] = useState(null);
  const [voucherNumber, setVoucherNumber] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [voucherProcessing, setVoucherProcessing] = useState(false);
  const [existingVouchers, setExistingVouchers] = useState([]);
  const [verifiedTotal, setVerifiedTotal] = useState(0);

  // Retirement states
  const [showRetireModal, setShowRetireModal] = useState(false);
  const [retireStep, setRetireStep] = useState(1);
  const [retireData, setRetireData] = useState({ amount_spent: '' });
  const [retireReceipt, setRetireReceipt] = useState(null);

  // Attachment preview state
  const [previewConfig, setPreviewConfig] = useState(null);
  const [retireVouchers, setRetireVouchers] = useState([]);
  const [retireVoucheredTotal, setRetireVoucheredTotal] = useState(0);
  const [retireProcessing, setRetireProcessing] = useState(false);

  // Detail modal voucher list
  const [detailVouchers, setDetailVouchers] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    purpose: '',
    category: 'Miscellaneous',
    priority: 'Normal',
    currency: currentOffice?.currency || 'TZS',
    department: '',
    items: [],
    // Recurring settings
    is_recurring: false,
    recurring_frequency: 'monthly',
    recurring_start_date: '',
    recurring_end_date: '',
    recurring_day: 1,
  });
  
  // New item state
  const [newItem, setNewItem] = useState({
    description: '',
    amount: '',
    quantity: 1,
    category: 'Miscellaneous',
    date_incurred: '',
    notes: '',
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchExpenses = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      const params = new URLSearchParams();
      if (activeTab !== 'all') params.append('status', activeTab);
      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      params.append('page', page);
      params.append('page_size', pageSize);
      
      const response = await fetch(`${API_URL}/api/expenses/my-requests?${params}`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        setExpenses(data.expenses || []);
        setSummary(data.summary || {});
        setTotalItems(data.total || 0);
        setTotalPages(data.total_pages || 1);
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, [activeTab, page, pageSize]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchExpenses();
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchDepartments();
    fetchCategories();
  }, []);

  const fetchDepartments = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      // Use the /list endpoint which extracts departments from employees
      const response = await fetch(`${API_URL}/api/employees/departments/list`, { headers });
      if (response.ok) {
        const data = await response.json();
        setDepartments(data || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/admin/expense-categories`, { headers });
      if (response.ok) {
        const data = await response.json();
        if (data.categories && data.categories.length > 0) {
          setCategories(data.categories.map(c => ({ value: c.name, label: c.name })));
        }
      }
    } catch (error) {
      // If endpoint doesn't exist or fails, use default categories
      console.error('Error fetching categories:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      purpose: '',
      category: 'Miscellaneous',
      priority: 'Normal',
      currency: currentOffice?.currency || 'TZS',
      department: '',
      items: [],
      // Recurring settings
      is_recurring: false,
      recurring_frequency: 'monthly',
      recurring_start_date: '',
      recurring_end_date: '',
      recurring_day: 1,
    });
    setNewItem({
      description: '',
      amount: '',
      quantity: 1,
      category: 'Miscellaneous',
      date_incurred: '',
      notes: '',
    });
    setEditingExpense(null);
  };

  // Load expense data into form for editing
  const loadExpenseForEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      title: expense.title || '',
      description: expense.description || '',
      purpose: expense.purpose || '',
      category: expense.category || 'Miscellaneous',
      priority: expense.priority || 'Normal',
      currency: expense.currency || currentOffice?.currency || 'TZS',
      department: expense.employee_department || '',
      items: (expense.items || []).map(item => ({
        ...item,
        id: item.id || Date.now().toString(),
      })),
      // Recurring settings
      is_recurring: expense.is_recurring || false,
      recurring_frequency: expense.recurring_frequency || 'monthly',
      recurring_start_date: expense.recurring_start_date || '',
      recurring_end_date: expense.recurring_end_date || '',
      recurring_day: expense.recurring_day || 1,
    });
    setShowCreateModal(true);
  };

  const addItem = () => {
    if (!newItem.description || !newItem.amount) {
      toast.error('Please fill in item description and amount');
      return;
    }
    
    const item = {
      ...newItem,
      id: Date.now().toString(),
      amount: parseFloat(newItem.amount),
      quantity: parseInt(newItem.quantity) || 1,
      total: parseFloat(newItem.amount) * (parseInt(newItem.quantity) || 1),
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, item],
    }));
    
    setNewItem({
      description: '',
      amount: '',
      quantity: 1,
      category: formData.category,
      date_incurred: '',
      notes: '',
    });
  };

  const removeItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(i => i.id !== itemId),
    }));
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const handleCreateExpense = async (submit = false) => {
    if (!formData.title) {
      toast.error('Please enter a title');
      return;
    }
    
    if (submit && formData.items.length === 0) {
      toast.error('Add at least one expense item before submitting');
      return;
    }
    
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      let response;
      
      if (editingExpense) {
        // UPDATE existing expense (for drafts and returned expenses)
        response = await fetch(`${API_URL}/api/expenses/${editingExpense.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            ...formData,
          }),
        });
        
        if (response.ok) {
          // If submit flag is true, also submit the expense after updating
          if (submit) {
            const submitResponse = await fetch(`${API_URL}/api/expenses/${editingExpense.id}/submit`, {
              method: 'POST',
              headers,
            });
            if (submitResponse.ok) {
              toast.success('Expense request updated and re-submitted for approval');
            } else {
              const error = await submitResponse.json();
              toast.error(error.detail || 'Updated but failed to submit');
            }
          } else {
            toast.success('Expense request updated');
          }
          setShowCreateModal(false);
          resetForm();
          fetchExpenses();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to update expense request');
        }
      } else {
        // CREATE new expense
        response = await fetch(`${API_URL}/api/expenses`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            ...formData,
            submit,
          }),
        });
        
        if (response.ok) {
          toast.success(submit ? 'Expense request submitted' : 'Expense request saved as draft');
          setShowCreateModal(false);
          resetForm();
          fetchExpenses();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to create expense request');
        }
      }
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error('Failed to save expense request');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (expenseId) => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      const response = await fetch(`${API_URL}/api/expenses/${expenseId}/submit`, {
        method: 'POST',
        headers,
      });
      
      if (response.ok) {
        toast.success('Expense request submitted for approval');
        fetchExpenses();
        setShowDetailModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit expense request');
      }
    } catch (error) {
      toast.error('Failed to submit expense request');
    }
  };

  const handleCancel = async (expenseId) => {
    try {
      const headers = getAuthHeaders();
      
      const response = await fetch(`${API_URL}/api/expenses/${expenseId}/cancel`, {
        method: 'POST',
        headers,
      });
      
      if (response.ok) {
        toast.success('Expense request cancelled');
        fetchExpenses();
        setShowDetailModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel expense request');
      }
    } catch (error) {
      toast.error('Failed to cancel expense request');
    }
  };

  const handleDelete = async (expenseId) => {
    if (!confirm('Are you sure you want to delete this expense request?')) return;
    
    try {
      const headers = getAuthHeaders();
      
      const response = await fetch(`${API_URL}/api/expenses/${expenseId}`, {
        method: 'DELETE',
        headers,
      });
      
      if (response.ok) {
        toast.success('Expense request deleted');
        fetchExpenses();
        setShowDetailModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete expense request');
      }
    } catch (error) {
      toast.error('Failed to delete expense request');
    }
  };

  const openDetail = (expense) => {
    setSelectedExpense(expense);
    setDetailVouchers([]);
    setShowDetailModal(true);

    if (expense.status === 'completed' || expense.status === 'retired') {
      (async () => {
        try {
          const headers = getAuthHeaders();
          const res = await fetch(`${API_URL}/api/payment-vouchers/by-expense/${expense.id}`, { headers });
          if (res.ok) {
            const data = await res.json();
            setDetailVouchers((data.vouchers || []).filter(v => v.status === 'verified'));
          }
        } catch {}
      })();
    }
  };

  const formatCurrency = (amount, currency = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // ── Payment Voucher Handlers ────────────────────────────────
  const openVoucherModal = async (expense) => {
    setSelectedExpense(expense);
    setVoucherStep('form');
    setVoucherData({
      payee_name: '', payee_phone: '', payee_email: '', amount_paid: '',
      purpose: '', payment_mode: 'cash', transfer_channel: '', transfer_account: '',
    });
    setOtpValue('');
    setVoucherId(null);
    setVoucherNumber('');
    setExistingVouchers([]);
    setVerifiedTotal(0);
    setShowVoucherModal(true);

    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/payment-vouchers/by-expense/${expense.id}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setExistingVouchers(data.vouchers || []);
        setVerifiedTotal(data.verified_total || 0);
        if (data.voucher) {
          // Always start fresh on form — pending vouchers get cancelled on new creation
        }
      }
    } catch {}
  };

  const handleCreateVoucher = async () => {
    if (!voucherData.payee_name || !voucherData.payee_phone || !voucherData.amount_paid || !voucherData.purpose) {
      toast.error('Please fill in all required fields');
      return;
    }
    const amt = parseFloat(voucherData.amount_paid);
    const expenseTotal = selectedExpense?.total_amount || 0;
    const remaining = expenseTotal - verifiedTotal;
    if (amt > expenseTotal) {
      toast.error(`Amount cannot exceed expense total (${expenseTotal.toLocaleString()})`);
      return;
    }
    if (amt > remaining) {
      toast.error(`Amount exceeds remaining balance (${remaining.toLocaleString()})`);
      return;
    }
    if (voucherData.payment_mode === 'transfer' && (!voucherData.transfer_channel || !voucherData.transfer_account)) {
      toast.error('Transfer channel and account number are required');
      return;
    }
    setVoucherProcessing(true);
    try {
      const headers = getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/payment-vouchers/create`, {
        method: 'POST', headers,
        body: JSON.stringify({ ...voucherData, amount_paid: amt, expense_id: selectedExpense.id }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setVoucherId(data.voucher_id);
        setVoucherNumber(data.voucher_number);
        setVoucherStep('otp');
        toast.success(data.otp_sent ? 'OTP sent to payee phone' : 'Voucher created but OTP delivery failed');
      } else {
        toast.error(data.detail || 'Failed to create voucher');
      }
    } catch { toast.error('Network error'); }
    finally { setVoucherProcessing(false); }
  };

  const handleVerifyOtp = async () => {
    setVoucherProcessing(true);
    try {
      const headers = getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const res = await fetch(`${API_URL}/api/payment-vouchers/${voucherId}/verify-otp`, {
        method: 'POST', headers,
        body: JSON.stringify({ otp: otpValue }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Payment voucher verified!');
        setVoucherStep('done');
      } else {
        toast.error(data.detail || 'Verification failed');
      }
    } catch { toast.error('Network error'); }
    finally { setVoucherProcessing(false); }
  };

  const handleResendOtp = async () => {
    setVoucherProcessing(true);
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/payment-vouchers/${voucherId}/resend-otp`, { method: 'POST', headers });
      const data = await res.json();
      toast[data.success ? 'success' : 'error'](data.message);
    } catch { toast.error('Network error'); }
    finally { setVoucherProcessing(false); }
  };

  const handleDownloadVoucherPdf = async (vid, vnum) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/payment-vouchers/${vid || voucherId}/pdf`, { headers });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Payment_Voucher_${vnum || voucherNumber}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } else { toast.error('Failed to download PDF'); }
    } catch { toast.error('Download error'); }
  };

  // ── Retirement Handlers ────────────────────────────────────
  const openRetireModal = async (expense) => {
    setSelectedExpense(expense);
    setRetireStep(1);
    setRetireData({ amount_spent: '' });
    setRetireReceipt(null);
    setRetireVouchers([]);
    setRetireVoucheredTotal(0);
    setShowRetireModal(true);

    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/payment-vouchers/by-expense/${expense.id}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setRetireVouchers((data.vouchers || []).filter(v => v.status === 'verified'));
        setRetireVoucheredTotal(data.verified_total || 0);
      }
    } catch {}
  };

  const handleRetireExpense = async () => {
    const amtSpent = parseFloat(retireData.amount_spent);
    if (!amtSpent || amtSpent <= 0) {
      toast.error('Please enter a valid amount spent');
      return;
    }
    setRetireProcessing(true);
    try {
      const headers = getAuthHeaders();
      delete headers['Content-Type'];
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const formData = new FormData();
      formData.append('amount_spent', amtSpent.toString());
      if (retireReceipt) formData.append('fiscal_receipt', retireReceipt);
      const res = await fetch(`${API_URL}/api/expenses/${selectedExpense.id}/retire`, {
        method: 'POST', headers, body: formData,
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Expense retired successfully');
        setShowRetireModal(false);
        setShowDetailModal(false);
        fetchExpenses();
      } else {
        toast.error(data.detail || 'Failed to retire expense');
      }
    } catch { toast.error('Network error'); }
    finally { setRetireProcessing(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6 p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Expenses</h1>
          <p className="text-gray-500 mt-1">Submit and track your expense requests</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} data-testid="create-expense-button">
          <Plus className="h-4 w-4 mr-2" />
          New Expense Request
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-200 rounded-lg">
                <FileText className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{summary.total_requests || 0}</p>
                <p className="text-xs text-gray-500">Total Requests</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-300 rounded-lg">
                <Edit className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-700">{summary.draft || 0}</p>
                <p className="text-xs text-gray-500">Drafts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-200 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-700">{summary.pending || 0}</p>
                <p className="text-xs text-yellow-600">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-200 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{summary.approved || 0}</p>
                <p className="text-xs text-green-600">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-200 rounded-lg">
                <XCircle className="h-5 w-5 text-red-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-700">{summary.rejected || 0}</p>
                <p className="text-xs text-red-600">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expenses List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(1); }}>
              <TabsList>
                <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
                <TabsTrigger value="draft" data-testid="tab-draft">Drafts</TabsTrigger>
                <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
                <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
                <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title, ID, category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="my-expense-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading...</p>
            </div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No expense requests</h3>
              <p className="text-gray-500 mt-1">{searchTerm ? 'No results match your search' : 'Create your first expense request to get started'}</p>
              {!searchTerm && (
                <Button onClick={() => setShowCreateModal(true)} className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  New Expense Request
                </Button>
              )}
            </div>
          ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Request ID</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow key={expense.id} className="cursor-pointer hover:bg-gray-50" onClick={() => openDetail(expense)}>
                    <TableCell>
                      <span className="font-mono text-sm text-blue-600">{expense.request_id || '-'}</span>
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {expense.title}
                        {expense.is_recurring && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded" title={`Recurring ${expense.recurring_frequency}`}>
                            <RefreshCw className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{expense.category}</TableCell>
                    <TableCell className="font-semibold">
                      {expense.currency} {formatCurrency(expense.total_amount)}
                    </TableCell>
                    <TableCell>
                      <Badge className={PRIORITY_COLORS[expense.priority]}>{expense.priority}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Badge className={STATUS_COLORS[expense.status] || STATUS_COLORS.pending}>
                          {STATUS_LABELS[expense.status] || expense.status}
                        </Badge>
                        {expense.status === 'returned' && (
                          <span className="text-orange-500" title="Needs revision and re-submission">
                            <AlertCircle className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {new Date(expense.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {(expense.status === 'draft' || expense.status === 'returned') && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={(e) => { e.stopPropagation(); loadExpenseForEdit(expense); }}
                            title="Edit"
                            data-testid={`edit-expense-${expense.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openDetail(expense); }}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination Controls */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t mt-4" data-testid="my-expense-pagination">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="border rounded-md px-2 py-1 text-sm bg-white"
                  data-testid="my-expense-page-size"
                >
                  {[10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span className="ml-2">
                  Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalItems)} of {totalItems}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => setPage(1)} data-testid="my-expense-page-first">
                  First
                </Button>
                <Button variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)} data-testid="my-expense-page-prev">
                  Prev
                </Button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) { pageNum = i + 1; }
                  else if (page <= 3) { pageNum = i + 1; }
                  else if (page >= totalPages - 2) { pageNum = totalPages - 4 + i; }
                  else { pageNum = page - 2 + i; }
                  return (
                    <Button key={pageNum} variant={page === pageNum ? 'default' : 'outline'} size="sm"
                      onClick={() => setPage(pageNum)}
                      className={page === pageNum ? 'bg-[#155179]' : ''}
                      data-testid={`my-expense-page-${pageNum}`}>
                      {pageNum}
                    </Button>
                  );
                })}
                <Button variant="outline" size="sm" disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)} data-testid="my-expense-page-next">
                  Next
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages}
                  onClick={() => setPage(totalPages)} data-testid="my-expense-page-last">
                  Last
                </Button>
              </div>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Expense Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => { if (!open) resetForm(); setShowCreateModal(open); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              {editingExpense ? 'Edit Expense Request' : 'New Expense Request'}
            </DialogTitle>
            <DialogDescription>
              {editingExpense 
                ? `Editing ${editingExpense.request_id || 'expense'} - Update details and ${editingExpense.status === 'returned' ? 're-submit' : 'save'}`
                : 'Fill in the details and add expense items'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Title *</Label>
                <Input
                  placeholder="e.g., Business Trip to Dar es Salaam"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  data-testid="expense-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Category</Label>
                <SearchableSelect
                  options={categories}
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                  placeholder="Select category"
                  searchPlaceholder="Search categories..."
                  data-testid="expense-category-select"
                />
              </div>

              <div className="space-y-2">
                <Label>Department</Label>
                <SearchableSelect
                  options={departments.map(d => ({ value: d.name, label: d.name }))}
                  value={formData.department}
                  onValueChange={(v) => setFormData({ ...formData, department: v })}
                  placeholder="Select department"
                  searchPlaceholder="Search departments..."
                  emptyText="No departments found"
                  data-testid="expense-department-select"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger data-testid="expense-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Normal">Normal</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-2 space-y-2">
                <Label>Business Purpose / Justification</Label>
                <Textarea
                  placeholder="Explain the business reason for this expense..."
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  rows={2}
                />
              </div>
            </div>

            {/* Recurring Expense Settings */}
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_recurring"
                  checked={formData.is_recurring}
                  onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Label htmlFor="is_recurring" className="text-sm font-medium text-blue-900 cursor-pointer">
                  Make this a recurring expense
                </Label>
                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                  Auto-creates new requests on schedule
                </span>
              </div>
              
              {formData.is_recurring && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3 border-t border-blue-200">
                  <div className="space-y-2">
                    <Label className="text-xs text-blue-800">Frequency *</Label>
                    <Select 
                      value={formData.recurring_frequency} 
                      onValueChange={(v) => setFormData({ ...formData, recurring_frequency: v })}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs text-blue-800">
                      {formData.recurring_frequency === 'weekly' || formData.recurring_frequency === 'biweekly' 
                        ? 'Day of Week' 
                        : 'Day of Month'}
                    </Label>
                    <Select 
                      value={String(formData.recurring_day)} 
                      onValueChange={(v) => setFormData({ ...formData, recurring_day: parseInt(v) })}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {formData.recurring_frequency === 'weekly' || formData.recurring_frequency === 'biweekly' ? (
                          <>
                            <SelectItem value="1">Monday</SelectItem>
                            <SelectItem value="2">Tuesday</SelectItem>
                            <SelectItem value="3">Wednesday</SelectItem>
                            <SelectItem value="4">Thursday</SelectItem>
                            <SelectItem value="5">Friday</SelectItem>
                            <SelectItem value="6">Saturday</SelectItem>
                            <SelectItem value="7">Sunday</SelectItem>
                          </>
                        ) : (
                          Array.from({ length: 28 }, (_, i) => (
                            <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs text-blue-800">Start Date *</Label>
                    <Input
                      type="date"
                      value={formData.recurring_start_date}
                      onChange={(e) => setFormData({ ...formData, recurring_start_date: e.target.value })}
                      className="bg-white"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs text-blue-800">End Date (Optional)</Label>
                    <Input
                      type="date"
                      value={formData.recurring_end_date}
                      onChange={(e) => setFormData({ ...formData, recurring_end_date: e.target.value })}
                      className="bg-white"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Expense Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Expense Items</Label>
                <Badge variant="outline" className="text-lg px-3 py-1">
                  Total: {formData.currency} {formatCurrency(calculateTotal())}
                </Badge>
              </div>
              
              {/* Add Item Form */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <div className="grid grid-cols-6 gap-3">
                  <div className="col-span-3 space-y-1">
                    <Label className="text-xs">Description *</Label>
                    <Input
                      placeholder="Item description"
                      value={newItem.description}
                      onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                      data-testid="item-description-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Amount *</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={newItem.amount}
                      onChange={(e) => setNewItem({ ...newItem, amount: e.target.value })}
                      data-testid="item-amount-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newItem.quantity}
                      onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button type="button" onClick={addItem} className="w-full" data-testid="add-item-button">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Date Incurred</Label>
                    <Input
                      type="date"
                      value={newItem.date_incurred}
                      onChange={(e) => setNewItem({ ...newItem, date_incurred: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Notes</Label>
                    <Input
                      placeholder="Optional notes"
                      value={newItem.notes}
                      onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Items List */}
              {formData.items.length > 0 && (
                <ScrollArea className="h-48 border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-center">Qty</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{item.description}</p>
                              {item.date_incurred && (
                                <p className="text-xs text-gray-500">{item.date_incurred}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                          <TableCell className="text-center">{item.quantity}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(item.total)}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => removeItem(item.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => { resetForm(); setShowCreateModal(false); }}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => handleCreateExpense(false)} disabled={saving}>
              {editingExpense ? 'Save Changes' : 'Save as Draft'}
            </Button>
            <Button onClick={() => handleCreateExpense(true)} disabled={saving || formData.items.length === 0}>
              <Send className="h-4 w-4 mr-2" />
              {editingExpense?.status === 'returned' ? 'Re-submit for Approval' : 'Submit for Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-2xl">
          {selectedExpense && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle>{selectedExpense.title}</DialogTitle>
                  <Badge className={STATUS_COLORS[selectedExpense.status]}>{STATUS_LABELS[selectedExpense.status] || selectedExpense.status}</Badge>
                </div>
                <DialogDescription className="flex items-center gap-2">
                  {selectedExpense.request_id && (
                    <span className="font-mono text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{selectedExpense.request_id}</span>
                  )}
                  {selectedExpense.request_id && <span>•</span>}
                  <span>{selectedExpense.purpose || 'No description provided'}</span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Summary */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-500">Category</p>
                    <p className="font-medium">{selectedExpense.category}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Priority</p>
                    <Badge className={PRIORITY_COLORS[selectedExpense.priority]}>{selectedExpense.priority}</Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Total Amount</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {selectedExpense.currency} {formatCurrency(selectedExpense.total_amount)}
                    </p>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <Label className="text-sm font-semibold">Expense Items</Label>
                  <ScrollArea className="h-48 mt-2 border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-center">Qty</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedExpense.items?.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{item.description}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                            <TableCell className="text-center">{item.quantity}</TableCell>
                            <TableCell className="text-right font-semibold">{formatCurrency(item.total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </div>

                {/* Status Info */}
                {selectedExpense.status === 'rejected' && selectedExpense.rejection_reason && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-800">Rejection Reason:</p>
                    <p className="text-sm text-red-700">{selectedExpense.rejection_reason}</p>
                    <p className="text-xs text-red-500 mt-1">
                      Rejected by {selectedExpense.rejected_by_name} on {new Date(selectedExpense.rejected_at).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {/* Returned/Sent Back Info */}
                {selectedExpense.status === 'returned' && (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-sm font-medium text-orange-800 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Request Returned for Revision
                    </p>
                    {selectedExpense.return_comment && (
                      <p className="text-sm text-orange-700 mt-2 bg-white p-2 rounded border border-orange-200">
                        "{selectedExpense.return_comment}"
                      </p>
                    )}
                    <p className="text-xs text-orange-500 mt-2">
                      Returned by {selectedExpense.returned_by_name} on {new Date(selectedExpense.returned_at).toLocaleDateString()}
                      {selectedExpense.returned_at_stage && ` (at ${selectedExpense.returned_at_stage} stage)`}
                    </p>
                    <p className="text-xs text-orange-600 mt-1 font-medium">
                      Please review the feedback, make necessary changes, and re-submit.
                    </p>
                  </div>
                )}

                {selectedExpense.status === 'approved' && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-medium text-green-800">Approved</p>
                    {selectedExpense.approval_notes && <p className="text-sm text-green-700">{selectedExpense.approval_notes}</p>}
                    <p className="text-xs text-green-500 mt-1">
                      Approved by {selectedExpense.approved_by_name} on {new Date(selectedExpense.approved_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>

              {/* Payment Vouchers */}
              {(selectedExpense.status === 'completed' || selectedExpense.status === 'retired') && detailVouchers.length > 0 && (
                <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg space-y-2 mt-3">
                  <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-[#0779BC]" /> Payment Vouchers ({detailVouchers.length})
                  </p>
                  {detailVouchers.map((v, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-white rounded px-3 py-2 border">
                      <span className="font-mono text-gray-500">{v.voucher_number}</span>
                      <span className="text-gray-600">{v.payee_name}</span>
                      <span className="font-semibold">{v.currency || 'TZS'} {(v.amount_paid || 0).toLocaleString()}</span>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-blue-700 hover:bg-blue-100"
                          onClick={() => setPreviewConfig({
                            title: `Payment Voucher — ${v.voucher_number}`,
                            downloadName: `Payment_Voucher_${v.voucher_number}.pdf`,
                            fetchUrl: `${API_URL}/api/payment-vouchers/${v.id}/pdf`,
                            mime: 'application/pdf',
                          })}>
                          <Eye className="h-3.5 w-3.5 mr-1" /> Preview
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-[#0779BC] hover:bg-blue-100"
                          onClick={() => handleDownloadVoucherPdf(v.id, v.voucher_number)}>
                          <Download className="h-3.5 w-3.5 mr-1" /> PDF
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Retirement Info */}
              {selectedExpense.status === 'retired' && selectedExpense.retirement && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg space-y-2 mt-3">
                  <p className="text-sm font-semibold text-orange-800 flex items-center gap-2">
                    <FileSearch className="h-4 w-4" /> Retirement Summary
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-white rounded p-2 border">
                      <span className="text-gray-500">Amount Requested</span>
                      <p className="font-bold">{selectedExpense.currency || 'TZS'} {(selectedExpense.retirement.amount_requested || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-white rounded p-2 border">
                      <span className="text-gray-500">Amount Spent</span>
                      <p className="font-bold">{selectedExpense.currency || 'TZS'} {(selectedExpense.retirement.amount_spent || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-white rounded p-2 border">
                      <span className="text-gray-500">Balance Returned</span>
                      <p className="font-bold">{selectedExpense.currency || 'TZS'} {(selectedExpense.retirement.balance_returned || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-white rounded p-2 border">
                      <span className="text-gray-500">Vouchered</span>
                      <p className="font-bold">{selectedExpense.currency || 'TZS'} {(selectedExpense.retirement.vouchered_total || 0).toLocaleString()}</p>
                    </div>
                  </div>
                  {selectedExpense.retirement?.fiscal_receipt_url && (
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" className="text-blue-700 border-blue-300 hover:bg-blue-100"
                        data-testid="preview-fiscal-receipt-btn"
                        onClick={() => {
                          const receiptUrl = selectedExpense.retirement.fiscal_receipt_url;
                          const ext = receiptUrl.split('.').pop()?.toLowerCase() || 'pdf';
                          setPreviewConfig({
                            title: `Fiscal Receipt — ${selectedExpense.request_id || selectedExpense.id}`,
                            downloadName: `Fiscal_Receipt_${selectedExpense.request_id || selectedExpense.id}.${ext}`,
                            fetchUrl: `${API_URL}${receiptUrl}`,
                          });
                        }}>
                        <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview Receipt
                      </Button>
                      <Button variant="outline" size="sm" className="text-orange-700 border-orange-300 hover:bg-orange-100"
                        data-testid="download-fiscal-receipt-btn"
                        onClick={async () => {
                          try {
                            const hdrs = getAuthHeaders();
                            delete hdrs['Content-Type'];
                            const receiptUrl = selectedExpense.retirement.fiscal_receipt_url;
                            const res = await fetch(`${API_URL}${receiptUrl}`, { headers: hdrs });
                            if (res.ok) {
                              const blob = await res.blob();
                              const ext = receiptUrl.split('.').pop() || 'pdf';
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `Fiscal_Receipt_${selectedExpense.request_id || selectedExpense.id}.${ext}`;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              URL.revokeObjectURL(url);
                            } else {
                              const errText = await res.text().catch(() => '');
                              console.error('Receipt download failed:', res.status, errText);
                              toast.error(`Failed to download receipt (${res.status})`);
                            }
                          } catch (err) { console.error('Download error:', err); toast.error('Download error: ' + err.message); }
                        }}>
                        <Download className="h-3.5 w-3.5 mr-1.5" /> Download Fiscal Receipt
                      </Button>
                    </div>
                  )}
                  {selectedExpense.retired_by_name && (
                    <p className="text-[10px] text-gray-500">Retired by {selectedExpense.retired_by_name} on {selectedExpense.retired_at?.slice(0,10)}</p>
                  )}
                </div>
              )}

              <DialogFooter className="flex gap-2 flex-wrap">
                {selectedExpense.can_cancel && (
                  <Button variant="outline" onClick={() => handleCancel(selectedExpense.id)}>
                    Cancel Request
                  </Button>
                )}
                {(selectedExpense.status === 'draft' || selectedExpense.status === 'returned') && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => { setShowDetailModal(false); loadExpenseForEdit(selectedExpense); }}
                      data-testid="detail-edit-expense"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      {selectedExpense.status === 'returned' ? 'Edit & Fix' : 'Edit'}
                    </Button>
                    <Button variant="destructive" onClick={() => handleDelete(selectedExpense.id)}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </>
                )}
                {selectedExpense.can_submit && (
                  <Button onClick={() => handleSubmit(selectedExpense.id)}>
                    <Send className="h-4 w-4 mr-2" />
                    {selectedExpense.status === 'returned' ? 'Re-submit for Approval' : 'Submit for Approval'}
                  </Button>
                )}
                {selectedExpense.status === 'completed' && (
                  <>
                    <Button variant="outline"
                      className="text-[#0779BC] border-[#0779BC] hover:bg-blue-50"
                      onClick={() => { setShowDetailModal(false); openVoucherModal(selectedExpense); }}
                    >
                      <Receipt className="h-4 w-4 mr-2" />
                      Payment Voucher
                    </Button>
                    <Button variant="outline"
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                      onClick={() => { setShowDetailModal(false); openRetireModal(selectedExpense); }}
                    >
                      <FileSearch className="h-4 w-4 mr-2" />
                      Retirement
                    </Button>
                  </>
                )}
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Voucher Modal */}
      <Dialog open={showVoucherModal} onOpenChange={setShowVoucherModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-[#0779BC]" />
              {voucherStep === 'done' ? 'Payment Voucher' : voucherStep === 'otp' ? 'Verify OTP' : 'Create Payment Voucher'}
            </DialogTitle>
            <DialogDescription>
              {voucherStep === 'form' && 'Enter payee details. An OTP will be sent to the payee for verification.'}
              {voucherStep === 'otp' && 'OTP sent to payee. Enter the code to verify.'}
              {voucherStep === 'done' && `Voucher ${voucherNumber} has been verified.`}
            </DialogDescription>
          </DialogHeader>

          {voucherStep === 'form' && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {existingVouchers.filter(v => v.status === 'verified').length > 0 && (
                <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg space-y-2">
                  <p className="text-xs font-semibold text-gray-700">Previous Vouchers ({existingVouchers.filter(v => v.status === 'verified').length})</p>
                  {existingVouchers.filter(v => v.status === 'verified').map((v, i) => (
                    <div key={i} className="flex items-center justify-between text-xs text-gray-600 bg-white rounded px-2 py-1.5">
                      <span className="font-mono">{v.voucher_number}</span>
                      <span>{v.payee_name}</span>
                      <span className="font-semibold">{v.currency || 'TZS'} {(v.amount_paid || 0).toLocaleString()}</span>
                      <button className="p-1 text-[#0779BC] hover:bg-blue-100 rounded" title="Download PDF"
                        onClick={() => handleDownloadVoucherPdf(v.id, v.voucher_number)}>
                        <Download className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <div className="flex justify-between text-xs pt-1 border-t border-blue-200">
                    <span className="text-gray-500">Total vouchered</span>
                    <span className="font-bold text-gray-800">{selectedExpense?.currency || 'TZS'} {verifiedTotal.toLocaleString()} / {(selectedExpense?.total_amount || 0).toLocaleString()}</span>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Payee Name <span className="text-red-500">*</span></Label>
                  <Input placeholder="Full name of payee" value={voucherData.payee_name}
                    onChange={(e) => setVoucherData({ ...voucherData, payee_name: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Phone <span className="text-red-500">*</span></Label>
                  <div className="flex">
                    <span className="inline-flex items-center px-2.5 bg-gray-100 border border-r-0 rounded-l-md text-xs text-gray-500">+255</span>
                    <Input placeholder="7XXXXXXXX" className="rounded-l-none" value={voucherData.payee_phone}
                      onChange={(e) => setVoucherData({ ...voucherData, payee_phone: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Email <span className="text-gray-400">(optional)</span></Label>
                  <Input placeholder="email@example.com" type="email" value={voucherData.payee_email}
                    onChange={(e) => setVoucherData({ ...voucherData, payee_email: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Amount Paid <span className="text-red-500">*</span></Label>
                  <Input type="number" placeholder="0" value={voucherData.amount_paid}
                    onChange={(e) => setVoucherData({ ...voucherData, amount_paid: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Mode of Payment <span className="text-red-500">*</span></Label>
                  <Select value={voucherData.payment_mode} onValueChange={(v) => setVoucherData({ ...voucherData, payment_mode: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="transfer">Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Purpose of Payment <span className="text-red-500">*</span></Label>
                <Textarea rows={2} placeholder="Describe the purpose..." value={voucherData.purpose}
                  onChange={(e) => setVoucherData({ ...voucherData, purpose: e.target.value })} />
              </div>
              {voucherData.payment_mode === 'transfer' && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div>
                    <Label className="text-xs">Transfer Channel <span className="text-red-500">*</span></Label>
                    <Input placeholder="e.g. Bank, M-Pesa" value={voucherData.transfer_channel}
                      onChange={(e) => setVoucherData({ ...voucherData, transfer_channel: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Account Number <span className="text-red-500">*</span></Label>
                    <Input placeholder="Account or phone number" value={voucherData.transfer_account}
                      onChange={(e) => setVoucherData({ ...voucherData, transfer_account: e.target.value })} />
                  </div>
                </div>
              )}
              <DialogFooter className="pt-3">
                <Button variant="outline" onClick={() => setShowVoucherModal(false)}>Cancel</Button>
                <Button className="bg-[#0779BC] hover:bg-[#065d94]" disabled={voucherProcessing} onClick={handleCreateVoucher}>
                  {voucherProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Send OTP & Create
                </Button>
              </DialogFooter>
            </div>
          )}

          {voucherStep === 'otp' && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
                <Phone className="h-8 w-8 text-[#0779BC] mx-auto mb-2" />
                <p className="text-sm text-gray-700">A 6-digit OTP has been sent to the payee's phone.</p>
                <p className="text-xs text-gray-500 mt-1">Voucher: <strong>{voucherNumber}</strong> — Valid for 10 minutes</p>
              </div>
              <div>
                <Label className="text-xs">Enter OTP from Payee</Label>
                <Input className="text-center text-2xl tracking-[0.5em] font-mono" maxLength={6} placeholder="------"
                  value={otpValue} onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))} />
              </div>
              <DialogFooter className="flex justify-between pt-2">
                <Button variant="ghost" size="sm" onClick={handleResendOtp} disabled={voucherProcessing}>Resend OTP</Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowVoucherModal(false)}>Cancel</Button>
                  <Button className="bg-green-600 hover:bg-green-700" disabled={voucherProcessing || otpValue.length < 6} onClick={handleVerifyOtp}>
                    {voucherProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BadgeCheck className="h-4 w-4 mr-2" />}
                    Verify OTP
                  </Button>
                </div>
              </DialogFooter>
            </div>
          )}

          {voucherStep === 'done' && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <BadgeCheck className="h-10 w-10 text-green-600 mx-auto mb-2" />
                <p className="text-lg font-bold text-green-800">Voucher Verified</p>
                <p className="text-sm text-gray-600 mt-1">{voucherNumber}</p>
              </div>
              <DialogFooter className="pt-2 flex-wrap gap-2">
                <Button variant="outline" onClick={() => setShowVoucherModal(false)}>Close</Button>
                {selectedExpense && (verifiedTotal + parseFloat(voucherData.amount_paid || 0)) < (selectedExpense.total_amount || 0) && (
                  <Button variant="outline" onClick={() => {
                    setVoucherStep('form');
                    setVoucherData({ payee_name: '', payee_phone: '', payee_email: '', amount_paid: '', purpose: '', payment_mode: 'cash', transfer_channel: '', transfer_account: '' });
                    setOtpValue(''); setVoucherId(null); setVoucherNumber('');
                    (async () => {
                      try {
                        const headers = getAuthHeaders();
                        const res = await fetch(`${API_URL}/api/payment-vouchers/by-expense/${selectedExpense.id}`, { headers });
                        if (res.ok) { const data = await res.json(); setExistingVouchers(data.vouchers || []); setVerifiedTotal(data.verified_total || 0); }
                      } catch {}
                    })();
                  }}>+ Create Another Voucher</Button>
                )}
                <Button className="bg-[#0779BC] hover:bg-[#065d94]" onClick={() => handleDownloadVoucherPdf()}>
                  <Download className="h-4 w-4 mr-2" /> Download PDF
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Retirement Modal */}
      <Dialog open={showRetireModal} onOpenChange={setShowRetireModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSearch className="h-5 w-5 text-orange-600" />
              Retire Expense
            </DialogTitle>
            <DialogDescription>
              {selectedExpense?.title} — {selectedExpense?.currency || 'TZS'} {(selectedExpense?.total_amount || 0).toLocaleString()}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 mb-2">
            <div className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${retireStep === 1 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
              {retireStep > 1 ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="w-4 h-4 rounded-full bg-orange-500 text-white flex items-center justify-center text-[10px]">1</span>}
              Amount Reconciliation
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-gray-300" />
            <div className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${retireStep === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-400'}`}>
              <span className="w-4 h-4 rounded-full bg-gray-300 text-white flex items-center justify-center text-[10px]">2</span>
              Voucher Reconciliation
            </div>
          </div>

          {retireStep === 1 && (() => {
            const requested = selectedExpense?.total_amount || 0;
            const spent = parseFloat(retireData.amount_spent) || 0;
            const balance = requested - spent;
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-[10px] uppercase text-gray-500 tracking-wide">Amount Requested</p>
                    <p className="text-lg font-bold text-gray-900">{selectedExpense?.currency || 'TZS'} {requested.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border">
                    <Label className="text-[10px] uppercase text-gray-500 tracking-wide">Amount Spent</Label>
                    <Input type="number" placeholder="0" className="mt-1 text-lg font-bold"
                      value={retireData.amount_spent}
                      onChange={(e) => setRetireData({ ...retireData, amount_spent: e.target.value })} />
                  </div>
                </div>
                {spent > 0 && (
                  <div className={`p-3 rounded-lg border ${balance > 0 ? 'bg-green-50 border-green-200' : balance < 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">Balance</span>
                      <span className={`text-base font-bold ${balance > 0 ? 'text-green-700' : balance < 0 ? 'text-red-700' : 'text-gray-700'}`}>
                        {selectedExpense?.currency || 'TZS'} {Math.abs(balance).toLocaleString()}
                        {balance > 0 ? ' to return' : balance < 0 ? ' overspent' : ' — exact match'}
                      </span>
                    </div>
                  </div>
                )}
                <DialogFooter className="pt-2">
                  <Button variant="outline" onClick={() => setShowRetireModal(false)}>Cancel</Button>
                  <Button className="bg-orange-600 hover:bg-orange-700" disabled={!spent || spent <= 0} onClick={() => setRetireStep(2)}>
                    Next <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </DialogFooter>
              </div>
            );
          })()}

          {retireStep === 2 && (() => {
            const spent = parseFloat(retireData.amount_spent) || 0;
            const unvouchered = spent - retireVoucheredTotal;
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2.5 bg-gray-50 rounded-lg border">
                    <p className="text-[10px] uppercase text-gray-500">Amount Spent</p>
                    <p className="text-sm font-bold">{selectedExpense?.currency || 'TZS'} {spent.toLocaleString()}</p>
                  </div>
                  <div className="p-2.5 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-[10px] uppercase text-gray-500">Vouchered</p>
                    <p className="text-sm font-bold text-[#0779BC]">{selectedExpense?.currency || 'TZS'} {retireVoucheredTotal.toLocaleString()}</p>
                  </div>
                  <div className={`p-2.5 rounded-lg border ${unvouchered > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                    <p className="text-[10px] uppercase text-gray-500">Balance</p>
                    <p className={`text-sm font-bold ${unvouchered > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                      {selectedExpense?.currency || 'TZS'} {Math.abs(unvouchered).toLocaleString()}
                    </p>
                  </div>
                </div>
                {retireVouchers.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-gray-600">Verified Vouchers ({retireVouchers.length})</p>
                    {retireVouchers.map((v, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2.5 py-1.5 border">
                        <span className="font-mono text-gray-500">{v.voucher_number}</span>
                        <span className="text-gray-600">{v.payee_name}</span>
                        <span className="font-semibold">{v.currency || 'TZS'} {(v.amount_paid || 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
                {unvouchered > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
                    <p className="text-xs text-amber-800 font-medium">
                      {selectedExpense?.currency || 'TZS'} {unvouchered.toLocaleString()} is not covered by payment vouchers. Upload fiscal receipt(s).
                    </p>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 px-3 py-1.5 bg-white border border-amber-300 rounded-md cursor-pointer hover:bg-amber-50 text-xs font-medium text-amber-700">
                        <Upload className="h-3.5 w-3.5" />
                        {retireReceipt ? retireReceipt.name : 'Choose PDF'}
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                          onChange={(e) => setRetireReceipt(e.target.files?.[0] || null)} />
                      </label>
                      {retireReceipt && <button className="text-xs text-red-500 hover:underline" onClick={() => setRetireReceipt(null)}>Remove</button>}
                    </div>
                  </div>
                )}
                {unvouchered <= 0 && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-xs text-green-700 font-medium flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4" /> All spent amounts are fully covered by payment vouchers.
                    </p>
                  </div>
                )}
                <DialogFooter className="pt-2 flex justify-between">
                  <Button variant="outline" onClick={() => setRetireStep(1)}>
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowRetireModal(false)}>Cancel</Button>
                    <Button className="bg-orange-600 hover:bg-orange-700"
                      disabled={retireProcessing || (unvouchered > 0 && !retireReceipt)}
                      onClick={handleRetireExpense}>
                      {retireProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                      Retire Expense
                    </Button>
                  </div>
                </DialogFooter>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <AttachmentPreviewModal
        open={!!previewConfig}
        onClose={() => setPreviewConfig(null)}
        title={previewConfig?.title || 'Attachment'}
        downloadName={previewConfig?.downloadName || 'attachment'}
        fetchUrl={previewConfig?.fetchUrl || ''}
        headers={getAuthHeaders()}
        mime={previewConfig?.mime}
      />
    </motion.div>
  );
};

export default MyExpenses;
